import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { IconComponent } from '@webapp/ui/components/icon/icon.component';

export type CameraViewState = 'requesting' | 'streaming' | 'denied' | 'error';

/**
 * Live browser camera capture (ADR-8 — supersedes ADR-4's native file input).
 * Requests `getUserMedia` on init, shows a live `<video>` preview, and captures
 * a frame to a `<canvas>` -> `Blob`. Hard-blocks with NO fallback (no file
 * picker) when permission is denied or the camera is unavailable/insecure —
 * see spec `Requirement: Photo Capture via Live Camera Only`.
 *
 * Moved from libs/activations/.../ui/camera-capture (design D1/D2) — now hosted
 * imperatively by WebPhotoCaptureService inside a CDK overlay instead of being
 * declared inline in photo-step's template.
 */
@Component({
  selector: 'app-camera-capture',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  templateUrl: './camera-capture.html',
})
export class CameraCaptureComponent implements OnInit, OnDestroy {
  readonly tip = input<string>();
  readonly captured = output<{ dataUrl: string; blob: Blob }>();

  readonly viewState = signal<CameraViewState>('requesting');

  private readonly videoRef = viewChild<ElementRef<HTMLVideoElement>>('video');
  private readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  private stream: MediaStream | null = null;

  ngOnInit(): void {
    void this.start();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  async start(): Promise<void> {
    this.viewState.set('requesting');

    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      this.viewState.set('error');
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });

      const video = this.videoRef()?.nativeElement;
      if (!video) {
        this.viewState.set('error');
        return;
      }

      video.srcObject = this.stream;
      video.muted = true;
      video.setAttribute('playsinline', 'true');
      await video.play();
      this.viewState.set('streaming');
    } catch (err) {
      const name = (err as { name?: string } | undefined)?.name;
      this.viewState.set(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'error');
    }
  }

  capture(): void {
    const video = this.videoRef()?.nativeElement;
    const canvas = this.canvasRef()?.nativeElement;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      blob => {
        if (blob) {
          this.captured.emit({ blob, dataUrl: canvas.toDataURL('image/jpeg', 0.9) });
        }
      },
      'image/jpeg',
      0.9,
    );

    this.stop();
  }

  retry(): void {
    void this.start();
  }

  private stop(): void {
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
  }
}
